import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { num, pct } from "@owners/format";
import { api } from "../api";
import { HotelPicker, hotelParam } from "../components/Numbers";

/**
 * Leadership (demo artboard; Academy feed, Genesis 11/02): each hotel's GM and
 * a 12-month scorecard — OCI and Brand QA (latest and average), training and
 * quiz scores, certifications. Academy doesn't track open leadership roles or
 * 90-day plans yet, so those parts of the demo aren't here. No pay, reviews or
 * HR notes ever reach the Owners Module.
 */

interface Score { oci_latest: number | null; oci_avg: number | null; brand_qa_latest: number | null; brand_qa_avg: number | null;
  training_assigned: number | null; training_completed: number | null; avg_quiz_score: number | null; certification_status: string | null }
interface Row { id: string; name: string; asOf: string | null; gm: { name: string | null; start_date: string | null; months_in_role: number | null } | null; scorecard: Score | null }
interface Res { connected: boolean; hotels: Row[] }

const p = (v: number | null | undefined) => (v == null ? "—" : pct(v, { ratio: true, decimals: 0 }));
const tenure = (m: number | null | undefined) => (m == null ? "—" : m < 12 ? `${m} mo` : `${Math.floor(m / 12)} yr${m % 12 ? ` ${m % 12} mo` : ""}`);
const mean = (xs: Array<number | null | undefined>) => { const v = xs.filter((x): x is number => x != null); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; };

export function Leadership() {
  const [ids, setIds] = useState<string[]>([]);
  const q = useQuery({ queryKey: ["leadership", ids], queryFn: () => api<Res>(`/api/leadership?hotelIds=${hotelParam(ids)}`) });
  const rows = q.data?.hotels ?? [];
  const sc = rows.map((r) => r.scorecard);
  const assigned = sc.reduce((t, s) => t + (s?.training_assigned ?? 0), 0), completed = sc.reduce((t, s) => t + (s?.training_completed ?? 0), 0);
  const withGm = rows.filter((r) => r.gm?.name).length;

  return (
    <>
      <div className="eyebrow">Owners Module · Leadership</div>
      <h1>GM scorecards</h1>
      <div className="row" style={{ marginBottom: 14 }}><HotelPicker value={ids} onChange={setIds} /></div>

      {q.isLoading ? <div className="card muted">Loading…</div> : q.error ? <div className="card error">{(q.error as Error).message}</div> : !q.data?.connected ? (
        <div className="card muted">Academy isn't connected yet.</div>
      ) : (
        <>
          <div className="tiles strip">
            <div className="card tile"><div className="eyebrow">OCI audit · latest</div><div className="tile-value">{p(mean(sc.map((s) => s?.oci_latest)))}</div><div className="tile-sub">average across hotels</div></div>
            <div className="card tile"><div className="eyebrow">Brand QA · latest</div><div className="tile-value">{p(mean(sc.map((s) => s?.brand_qa_latest)))}</div><div className="tile-sub">average across hotels</div></div>
            <div className="card tile"><div className="eyebrow">Training</div><div className="tile-value">{assigned ? p(completed / assigned) : "—"}</div><div className="tile-sub">{num(completed)} of {num(assigned)} completed · 12 months</div></div>
            <div className="card tile"><div className="eyebrow">Quiz average</div><div className="tile-value">{p(mean(sc.map((s) => s?.avg_quiz_score)))}</div><div className="tile-sub">{num(withGm)} of {num(rows.length)} hotels with a GM on file</div></div>
          </div>

          <div className="card">
            <table className="fin">
              <thead><tr><th>Hotel</th><th>General manager</th><th className="num">In role</th>
                <th className="num">OCI latest</th><th className="num">OCI 12-mo avg</th><th className="num">Brand QA latest</th><th className="num">Brand QA avg</th>
                <th className="num">Training</th><th className="num">Quiz avg</th><th>Certifications</th></tr></thead>
              <tbody>
                {rows.map((r) => {
                  const s = r.scorecard;
                  return (
                    <tr key={r.id}>
                      <td>{r.name}</td>
                      <td>{r.gm?.name ?? <span className="muted">Not on file</span>}</td>
                      <td className="num">{tenure(r.gm?.months_in_role)}</td>
                      <td className="num">{p(s?.oci_latest)}</td><td className="num">{p(s?.oci_avg)}</td>
                      <td className="num">{p(s?.brand_qa_latest)}</td><td className="num">{p(s?.brand_qa_avg)}</td>
                      <td className="num">{s?.training_assigned ? `${num(s.training_completed ?? 0)} / ${num(s.training_assigned)}` : "—"}</td>
                      <td className="num">{p(s?.avg_quiz_score)}</td>
                      <td>{s?.certification_status ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="muted" style={{ fontSize: 12 }}>
              From Oliver Academy, last 12 months. Blank = not entered in Academy yet. Open leadership roles and 90-day plans
              will appear when Academy tracks them. No pay, reviews or HR notes are shared with the Owners Module.
            </p>
          </div>
        </>
      )}
    </>
  );
}
